/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MdOutlinePlaylistPlay } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { cn } from "~/lib/utils";
import { BLUR_HASH, COLORS, FALLBACK_IMAGE_URL } from "~/utils/constant";
import { convertSecondsToHMS, mediaAudioOnly } from "~/utils/helper-funcations";
import { PropsType } from "../audio-video/VideoCard";
import AudioIcon from "../shared-components/AudioIcon";
import ClientOnly from "../shared-components/ClentOnly";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import Tag from "../shared-components/Tag";
import { DISPLAY_TYPE } from "../video-grid/video-data";

function SquareCard({ className, data, isChildrenCard = false }: PropsType) {
  const isPlaylist = data?.node_type ? data?.node_type !== DISPLAY_TYPE.branch : false;
  const isBranch = data?.node_type ? data?.node_type == DISPLAY_TYPE.branch : false;
  const isAudioOnly = mediaAudioOnly(data?.media_type!);

  const { normalInter } = useFontStyle();
  const { t } = useTranslation();

  const ImageCard = () => (
    <ImageWithSkeleton
      url={{
        small:
          data?.square_img?.s?.url || data?.img?.s?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "low")) || FALLBACK_IMAGE_URL,
        medium:
          data?.square_img?.m?.url ||
          data?.img?.m?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) ||
          FALLBACK_IMAGE_URL,
        large:
          data?.square_img?.l?.url ||
          data?.img?.l?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) ||
          FALLBACK_IMAGE_URL,
        hd:
          data?.square_img?.hd?.url ||
          data?.img?.hd?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "high")) ||
          FALLBACK_IMAGE_URL,
        original:
          data?.square_img?.og?.url ||
          data?.img?.og?.url ||
          (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "maximum")) ||
          FALLBACK_IMAGE_URL,
      }}
      alt="thumbnail"
      className="w-full h-full object-cover"
      roundedClass="rounded-xl"
      blur_hash={data?.square_img?.bh || BLUR_HASH}
      isPreloaded={true}
    />
  );

  return (
    <div className={`w-full flex flex-col gap-1 justify-center items-center cursor-pointer relative group ${className}`}>
      {/* Thumbnail Section */}
      {isPlaylist && (
        <>
          <div
            className="absolute -top-2.5 xs:-top-2 left-1.5 md:left-4 2xl:left-2 xs:w-[84%] md:w-[75%]  2xl:w-[88%] h-[10%] bg-black/30 rounded-xl translate-y-[4px] translate-x-[4px] z-0 opacity-50"
            style={{ backgroundImage: `url(${data?.square_img?.og?.url || FALLBACK_IMAGE_URL})` }}
          ></div>
          {/* <div className="absolute -top-3.5 xs:-top-2.5 xs:left-3 md:left-6 lg:left-4 2xl:left-4 xs:w-[80%] md:w-[70%]  lg:w-[79%] 2xl:w-[80%] h-[10%] bg-black/20 rounded-xl translate-y-[2px] translate-x-[2px] z-0"></div> */}
        </>
      )}
      {/* Main Thumbnail */}
      <div
        className={cn(
          " md:h-36 lg:h-36 2xl:h-40 aspect-square relative overflow-hidden z-10 rounded-xl bg-gray-300",
          isChildrenCard ? "xs:h-32 smd:h-40 sml:h-32  " : " xs:h-28 smd:h-32 sml:h-32 md:h-28 lg:h-32 ",
        )}
      >
        <ClientOnly fallback={<ImageCard />}>
          <ImageCard />
        </ClientOnly>

        {data?.tag && <div className="w-auto absolute top-2 right-2">{<Tag text={data?.tag} />}</div>}
        {isAudioOnly && <AudioIcon isVisible={isAudioOnly} className="absolute bottom-2 left-2" />}

        {/* Playlist Tag */}
        {isPlaylist ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.part} {t(`common.videos`, { defaultValue: "Videos" })}
            </span>
          </div>
        ) : data?.duration_sec ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center justify-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-10 xs:min-w-[40px] md:min-w-[40px]">
            <span className={cn(normalInter)}>{convertSecondsToHMS(data?.duration_sec || 0)}</span>
          </div>
        ) : isBranch ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.playlist_item_count} {t(`common.playlist`, { defaultValue: "Playlist" })}
            </span>
          </div>
        ) : null}
      </div>

      {/* Video Details */}
      <div className="w-full flex flex-col justify-start items-start px-3">
        <p
          className={cn(
            "common-text-size text-textColorPrimary line-clamp-2 leading-5 overflow-hidden group-hover:text-darkOrangeColor/80 transition-colors duration-200 ease-in-out w-full",
            normalInter,
          )}
          title={data?.name}
        >
          {data?.name || ""}
        </p>

        {(data?.duration_sec || data?.record_date) && (
          <div className="flex justify-start items-center gap-2">
            {data?.record_date && (
              <p className={cn("body_text_xs text-textSecondaryColor", normalInter)}>{dayjs(data?.record_date).format("DD MMM YYYY")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SquareCard;
